import React from "react";
import Testimonials from "@/components/Testimonials";
import Testimonial from "@/components/Testimonials/Testimonial";
import LearnerTestimonials from "@/components/CourseContent/LearnerTestimonials";
import { DEFAULT_TESTIMONIALS, DEFAULT_AUTHOR } from "./constants";
import { MonarchTestimonialProps } from "@/monarch/types/MonarchTestimonial";
import AlumniTestimonialsSlider from "./AlumniTestimonialsSlider";
import { cn } from "@/lib/utils";
import useTranslations from "@/i18n/useTranslations";

interface AlumniTestimonialsProps {
  // Monarch testimonials (alumni testimonials)
  monarchTestimonials?: MonarchTestimonialProps;
  // Database testimonials (program.individualEndorsements)
  programTestimonials?: {
    quote: string | null;
  }[];
  // Database testimonials (course.learnerTestimonials)
  courseTestimonials?: string;
  useSlider?: boolean;
  // When true, backfill to 3 with defaults if Monarch has fewer
  fillWithDefaults?: boolean;
  testimonialContainerClassName?: string;
  headingClassName?: string;
  titleClassName?: string;
}

export default function AlumniTestimonials({
  monarchTestimonials,
  programTestimonials,
  courseTestimonials,
  testimonialContainerClassName,
  headingClassName,
  useSlider: useSliderProp = false,
  fillWithDefaults = true,
  titleClassName = "text-start",
}: AlumniTestimonialsProps) {
  const t = useTranslations("Course");
  const useSlider = monarchTestimonials?.useSlider ?? useSliderProp;
  // Priority 1: Monarch testimonials
  if (monarchTestimonials?.items && monarchTestimonials.items.length > 0) {
    const testimonials = monarchTestimonials.items.map((item, index) => ({
      author: item?.author || DEFAULT_AUTHOR,
      quote: item?.quote || DEFAULT_TESTIMONIALS[index]?.quote,
    }));

    // Use slider if useSlider is true and more than 3 testimonials, otherwise use regular Testimonials
    if (useSlider && testimonials.length > 3) {
      return (
        <AlumniTestimonialsSlider
          title={monarchTestimonials.title}
          testimonials={testimonials}
          testimonialCardClassName={testimonialContainerClassName}
          titleClassName={titleClassName}
        />
      );
    }

    const numberOfCards = fillWithDefaults
      ? 3
      : Math.min(3, monarchTestimonials.items.length);

    return (
      <Testimonials
        heading={monarchTestimonials.title}
        showReviewHeadline={true}
        title=""
        variant="light"
        titleAlign="left"
        className={cn(
          "bg-putty-200 border-putty-400 [&_p]:text-gray-800",
          testimonialContainerClassName,
        )}
        show={numberOfCards}
        headingClassName={cn("[&_h2]:!mt-0 py-0", headingClassName)}
        titleClassName={`mt-0 mb-10 ${titleClassName}`}
      >
        {Array.from({ length: numberOfCards }, (_, index) => {
          const monarchItem = monarchTestimonials.items[index];

          // Use Monarch data if available, otherwise use defaults
          const author = monarchItem?.author || DEFAULT_AUTHOR;
          const quote =
            monarchItem?.quote || DEFAULT_TESTIMONIALS[index]?.quote;
          return (
            <Testimonial
              key={`testimonial-${index}`}
              id={`testimonial-${index}`}
              attribution={`— ${author}`}
              variant="light"
              showQuote={true}
              testimonialCardClassName={testimonialContainerClassName}
            >
              <p className="text-base leading-relaxed">{quote}</p>
            </Testimonial>
          );
        })}
      </Testimonials>
    );
  }

  // Priority 2: Database testimonials (individualEndorsements)
  if (programTestimonials && programTestimonials.length > 0) {
    const learnerTestimonials = programTestimonials
      .filter((endorsement) => endorsement.quote)
      .map((endorsement) => ({
        textRaw: endorsement.quote || "",
        author: "",
      }));

    if (learnerTestimonials.length > 0) {
      return (
        <LearnerTestimonials
          title={t("learnerTestimonials")}
          testimonials={learnerTestimonials}
          trackEvents={true}
          titleClassName={titleClassName}
        />
      );
    }
  }

  if (courseTestimonials && courseTestimonials.length > 0) {
    return (
      <LearnerTestimonials
        title={t("learnerTestimonials")}
        testimonials={courseTestimonials}
        trackEvents={true}
        titleClassName={titleClassName}
      />
    );
  }

  return null;
}
